import { Component, Host, h, Prop, State, Listen, Watch, Fragment } from '@stencil/core';
import { FormEvent, LoginEvent, CreateUserEvent, PianoTerm, InterestResponse, newsletters, NotificationsInterestsInfo, Step, ErrorType, gtmEvent } from './types';
import { requestEmailCheck, requestUserCreation, requestUserLogin, sendCompleteExternalProfile } from './requests';
import PlanHeader from './components/plan-header';
import { getCookie, removeCookie, setCookie } from './utils';
import type { Site } from '../../types/common';

const STEPS = ["provider", "registration", "payment", "thanks", "interests", "newsletters", "recover", "check-inbox", "email-verification"];
const STEPS_WITH_CLOSE_BUTTON = ["provider", "thanks", "interests", "newsletters", "recover", "check-inbox"];

const DEFAULT_ENDPOINTS = {
  checkEmailUrl: '/accounts/check',
  signupUrl: '/accounts/sign_up',
  loginUrl: '/accounts/sign_in',
  googleSignInUrl: '/accounts/google/sign_in',
  facebookSignInUrl: '/accounts/facebook/sign_in',
  externalProfileUrl: '/users/external_profile',
  subscribeToNewslettersUrl: '/accounts/newsletters_subscribe',
  recoverPasswordUrl: '/accounts/forgot_account'
}

@Component({
  tag: 'hd-auth',
  styleUrl: 'hd-auth.pcss',
  shadow: true,
})
export class HdAuth {
  @Prop({ mutable: true, reflect: true }) open = false;
  @Prop() customStart?: Step;
  @Prop({ mutable: true }) type: "signup" | "login" | "external_profile" | "recover" | "check-inbox" = "signup";
  @Prop({ mutable: true }) eventSource: string = '';
  @Prop({ mutable: true }) experienceId: string = '';

  /* API options */
  @Prop() prependAPI: string = window.AD?.prepend_api || "";
  @Prop() checkEmailUrl: string = DEFAULT_ENDPOINTS.checkEmailUrl;
  @Prop() signupUrl: string = DEFAULT_ENDPOINTS.signupUrl;
  @Prop() loginUrl: string = DEFAULT_ENDPOINTS.loginUrl;
  @Prop() googleSignInUrl: string = DEFAULT_ENDPOINTS.googleSignInUrl;
  @Prop() facebookSignInUrl: string = DEFAULT_ENDPOINTS.facebookSignInUrl;
  @Prop() externalProfileUrl: string = DEFAULT_ENDPOINTS.externalProfileUrl;
  @Prop() subscribeToNewslettersUrl: string = DEFAULT_ENDPOINTS.subscribeToNewslettersUrl;
  @Prop() recoverPasswordUrl: string = DEFAULT_ENDPOINTS.recoverPasswordUrl;
  @Prop() campusVersion: boolean = false;
  @Prop({ mutable: true }) paymentDetails: { experienceId: string, offerId: string, termId: string, term: PianoTerm } = null;
  @Prop() site: Site = 'us';
  @Prop({ mutable: true }) email: string = '';

  @State() consent: boolean | null = null;
  @State() emailExists: boolean | null = null;
  @State() interests: InterestResponse;
  @State() selectedInterests: NotificationsInterestsInfo[] = [];
  @State() selectedNewsletters: string[] = [];
  @State() provider: string = '';
  @State() loggedIn: boolean = false;
  @State() closeReason: string = 'unknown';

  @State() stepIndex: number = 0;
  @State() externalProvider: string;
  @State() redirectUrl: string;
  @State() globalError: ErrorType | null = null;
  @State() localErrors: Record<string, string> = {};

  componentWillLoad() {
    const searchParams = new URLSearchParams(window.location.search);
    this.externalProvider = searchParams.get("provider");
    this.redirectUrl = searchParams.get("callback");
  }

  componentDidLoad() {
    if (this.type === "external_profile") {
      this.stepIndex = STEPS.indexOf("registration");
      const paymentDetails = getCookie("_pc_payment_details");
      if (paymentDetails) {
        this.paymentDetails = JSON.parse(paymentDetails);
      }
    } else if (this.customStart) {
      this.stepIndex = STEPS.indexOf(this.customStart);
    }
  }

  @Listen("kenneth:rendered")
  updatePrependApi() {
    this.prependAPI = window.AD.prepend_api;
  }

  @Listen("nextStep")
  nextStep(event?: CustomEvent<FormEvent>) {
    this.email = event?.detail?.email ?? "";
    switch (STEPS[this.stepIndex]) {
      case "provider":
        if (this.type === "signup" && this.email && this.emailExists === null) {
          this.checkEmail(this.email);
        } else if (this.type === "login" && event.detail.email && event.detail.password) {
          this.loginUser(event.detail as LoginEvent);
        }
        return;
      case "registration":
        if (this.type === "external_profile") {
          this.completeExternalProfile(event.detail as CreateUserEvent);
        } else if (this.emailExists === false || this.campusVersion) {
          this.createUser(event.detail as CreateUserEvent);
        }
        return;
    }

    this.stepIndex++;
  }

  @Listen("recoverStep")
  recoveryStep(event?: CustomEvent<string>) {
    switch (STEPS[this.stepIndex]) {
      case "provider":
        this.type = "recover"
        this.stepIndex = STEPS.indexOf(this.type);
        break;
      case "recover":
        this.type = "signup";
        this.stepIndex = STEPS.indexOf("provider");
        break;
      case "check-inbox":
        if (event && event?.detail["type"] === "provider") {
          this.type = "login";
          this.stepIndex = STEPS.indexOf(event.detail["type"]);
        } else {
          this.stepIndex = STEPS.indexOf(event.detail["type"]);
        }
        break;
    }
    return;
  }

  @Listen("prevStep")
  prevStep() {
    this.globalError = null;
    if (this.stepIndex <= 1) {
      this.emailExists = null;
      this.stepIndex = STEPS.indexOf("provider");
      return;
    }
    this.stepIndex--;
  }

  @Listen("retryStep")
  retryStep() {
    this.globalError = null;
    if (this.stepIndex === STEPS.indexOf("provider")) {
      this.emailExists = null;
    }
  }

  async checkEmail(email: string) {
    this.emailExists = null;

    const url = this.prependAPI + this.checkEmailUrl;
    requestEmailCheck(url, email, (emailExists) => {
      this.emailExists = emailExists;
      if (this.emailExists === true) {
        this.type = "login";
        return;
      }
      this.stepIndex = STEPS.indexOf("registration");
    });
  }

  @Listen("interestsChanged")
  handleInterestsChanged(event: CustomEvent<NotificationsInterestsInfo[]>) {
    this.selectedInterests = event.detail;
  }

  @Listen("newslettersChanged")
  handleNewslettersChanged(event: CustomEvent<string[]>) {
    this.selectedNewsletters = event.detail;
  }

  @Watch("open")
  openChanged(openNewValue: boolean) {
    if (openNewValue) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }

  @Watch("email")
  emailChanged(newEmail: string) {
    this.email = newEmail;
    this.emailExists = null;
  }

  @Watch("customStart")
  customStartChanged(newCustomStart: Step) {
    this.customStart = newCustomStart;
    this.stepIndex = STEPS.indexOf(newCustomStart);
  }

  @Watch("paymentDetails")
  paymentDetailsChanged(paymentDetails: { experienceId: string, offerId: string, termId: string, term: PianoTerm }) {
    this.paymentDetails = paymentDetails;
    setCookie("_pc_payment_details", JSON.stringify(paymentDetails), { days: 1 });
  }

  async createUser({ email, full_name, occupation, years_of_experience, country_code, password, token }: CreateUserEvent) {
    try {
      this.globalError = null;
      const url = this.prependAPI + this.signupUrl;
      let redirectAfterCreation = undefined;

      if (this.campusVersion) {
        const url = new URL(window.location.href);
        url.search = ""; // Remove piano params
        url.searchParams.set("checkout", "true");
        redirectAfterCreation = url.toString();
      }

      const data = await requestUserCreation(url,
        { email, full_name, occupation, password, token, years_of_experience, country_code },
        redirectAfterCreation);

      if (data.jwt_token) {
        window.tp?.push(["setExternalJWT", data.jwt_token]);

        if (window.tp && this.paymentDetails && !this.campusVersion) {
          this.startCheckout();
        } else if (this.campusVersion) {
          this.stepIndex = STEPS.indexOf("email-verification");
        } else {
          this.stepIndex = STEPS.indexOf("thanks");
        }
      } else {
        throw new Error("No jwt_token received");
      }
    } catch (error) {
      console.error("Error creating user", error);
      this.globalError = "signup"
    }
  }

  completeExternalProfile = async ({ full_name, occupation, years_of_experience, country_code, email }: CreateUserEvent) => {
    try {
      const self = this;
      this.globalError = null;
      const { uid, provider, app_id, callback } = Object.fromEntries(new URLSearchParams(window.location.search));
      if (!uid || !provider || !app_id || !callback) {
        console.error('Missing external profile parameters');
      }
      const authenticityToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
      const values = {
        "user[full_name]": full_name,
        "user[ocupation]": occupation,
        "user[years_of_experience]": years_of_experience,
        "user[country]": country_code,
        uid, provider, app_id, callback, authenticity_token: authenticityToken, skip_interests: true
      };

      if (this.externalProvider === "facebook") {
        values["user[email]"] = email;
      }

      const url = this.prependAPI + this.externalProfileUrl;
      const data = await sendCompleteExternalProfile(url, values);

      if (!data.token) {
        console.error("No token received");
        return;
      }
      if (data.redirect_to) {
        this.redirectUrl = data.redirect_to;
      }

      if (!this.paymentDetails) {
        self.email = data.email;
        self.stepIndex = STEPS.indexOf("thanks");
        return;
      }

      window.initializePiano?.(data.token, () => {
        self.startCheckout();
      });

    } catch (error) {
      this.globalError = "profile_completion";
    }
  }

  async loginUser({ email, password }: { email: string, password: string }) {
    try {
      this.globalError = null;
      const url = this.prependAPI + this.loginUrl;
      const data = await requestUserLogin(url, { email, password });
      if (data.subscription === "premium" || !this.paymentDetails) {
        window.location.reload();
        return;
      }
      if (this.paymentDetails) {
        window.tp.push(["setExternalJWT", data.jwt_token]);
        this.startCheckout();
      }
    } catch (error) {
      if (error.message.includes("pass:Invalid password")) {
        this.localErrors = {
          "password": "Invalid password. Did you forget your password?"
        };
      } else {
        console.error("loginUser error", error);
        this.globalError = "login";
      }
    }
  }

  async startCheckout() {
    if (!this.paymentDetails) {
      console.error('No offer or term provided');
      return;
    }
    const self = this;
    this.stepIndex = STEPS.indexOf("payment");

    window.tp.push(["addHandler", "checkoutComplete", function (_event) {
      window.tp.offer.close();
      self.stepIndex = STEPS.indexOf("thanks");
      return false;
    }]);

    window.tp.push(["addHandler", "checkoutClose", function (event) {
      if (event.state === "close") {
        self.onClose();
        return false;
      }
    }]);

    const backdrop = document.querySelector(".tp-backdrop") as HTMLElement;
    if (backdrop) {
      backdrop.style.display = "none";
    }
    window.tp.offer.startCheckout({ experienceId: this.paymentDetails.experienceId, offerId: this.paymentDetails.offerId, termId: this.paymentDetails.termId });
  }

  onClose = async (e?: Event) => {
    e?.stopPropagation();

    // Dispatch GTM tracking event for popup close
    this.dispatchCloseEvent(e);

    removeCookie("_pc_payment_details");
    if (!!this.externalProvider) {
      if (this.stepIndex === STEPS.indexOf("registration")) {
        return false;
      }

      if (this.stepIndex >= STEPS.indexOf("payment")) {
        window.location.href = this.redirectUrl;
        return false;
      }
    }

    if (this.stepIndex >= STEPS.indexOf("payment")) {
      window.location.reload();
      return;
    }

    this.open = false;
    this.emailExists = null;
    this.paymentDetails = null;
    this.globalError = null;
    this.localErrors = {};
    this.customStart = undefined;
    this.stepIndex = 0;
    this.eventSource = '';
    this.experienceId = '';
  }

  @Watch("loggedIn")
  loggedInChanged(newValue: boolean) {
    if (newValue) this.dispatchCloseEvent();
  }

  private dispatchCloseEvent = (e?: Event) => {
    // Determine the close trigger
    const closeReason = this.getCloseReason(e);

    // Prepare the GTM data layer event
    const gtmEvent: gtmEvent = {
      category: 'hadid-auth',
      action: '2000,close-modal,custom-event',
      label: 'hadid-auth-modal',
      dim1: closeReason || this.closeReason,
      dim2: STEPS[this.stepIndex],
      dim3: this.type,
      dim4: this.email || window.AD?.CurrentUser?.email || null,
      dim5: !!this.paymentDetails,
      dim6: this.paymentDetails?.term?.billingPlanTable[0]?.period || null,
      dim7: this.paymentDetails?.term?.resource?.name || null,
      dim8: this.paymentDetails?.experienceId || this.experienceId || null,
      dim9: this.eventSource || null,
      dim10: this.externalProvider || this.provider || null
    };

    // Dispatch the postMessage for GTM
    window.postMessage({
      type: 'hadid_event',
      data: gtmEvent
    }, '*');
  }

  private getCloseReason = (e?: Event): string => {
    if (this.closeReason) return this.closeReason;
    if (!e) return 'programmatic';

    const target = e.target as HTMLElement;

    // Check if it's the backdrop overlay
    if (target?.classList?.contains('backdrop-overlay')) {
      return 'overlay_click';
    }

    // Check if it's the close button or contains close button
    if (target?.classList?.contains('close-button') ||
      target?.closest('.close-button')) {
      return 'close_button';
    }

    return 'unknown';
  }

  render() {
    return (
      <Host
        class={{
          'open': this.open,
        }}
        aria-hidden={!this.open}
      >
        <div class="backdrop-overlay" onClick={this.onClose}></div>
        <div class={{
          'auth-container': true,
          'with-payment': !!this.paymentDetails,
        }}>
          {STEPS_WITH_CLOSE_BUTTON.includes(STEPS[this.stepIndex]) && (
            <button class="close-button" onClick={this.onClose}>
              <hd-icon svg="cancel" width="12" height="12"></hd-icon>
            </button>
          )}

          {this.globalError !== null ?
            <hd-auth-error errorType="signup" />
            :
            <Fragment>
              {this.paymentDetails && this.stepIndex < STEPS.indexOf("payment") && (
                <PlanHeader term={this.paymentDetails.term} />
              )}

              {STEPS[this.stepIndex] === 'provider' && this.type !== "external_profile" && (
                <hd-auth-provider
                  type={this.type}
                  googleSignInUrl={this.googleSignInUrl}
                  facebookSignInUrl={this.facebookSignInUrl}
                  localErrors={this.localErrors}
                  onProvider={e => this.provider = e.detail}
                  onLoggedInChange={e => this.loggedIn = e.detail}
                ></hd-auth-provider>
              )}
              {STEPS[this.stepIndex] === 'registration' && (this.emailExists === false || this.type === "external_profile" || this.campusVersion) &&
                <hd-registration type={this.type} email={this.email} campusVersion={this.campusVersion} eventSource={this.eventSource}
                  paymentDetails={this.paymentDetails} experienceId={this.experienceId} />}

              {STEPS[this.stepIndex] === 'email-verification' &&
                <hd-email-verification email={this.email}></hd-email-verification>}

              {STEPS[this.stepIndex] === 'thanks' &&
                <hd-confirmation email={this.email} paymentDetails={this.paymentDetails}></hd-confirmation>}

              {/* Payment step provided by Piano */}

              {STEPS[this.stepIndex] === 'interests' &&
                <hd-interests selectedInterests={this.selectedInterests} site={this.site}></hd-interests>}
              {STEPS[this.stepIndex] === 'newsletters' &&
                <hd-newsletters
                  newsletters={newsletters}
                  site={this.site}
                  selectedNewsletters={this.selectedNewsletters}
                  onClose={this.onClose}
                  url={this.prependAPI + this.subscribeToNewslettersUrl}
                  onCloseReason={e => this.closeReason = e.detail}>
                </hd-newsletters>}
              {STEPS[this.stepIndex] === 'recover' &&
                <hd-recover url={this.prependAPI + this.recoverPasswordUrl} />}
              {STEPS[this.stepIndex] === 'check-inbox' &&
                <hd-check-inbox
                  email={this.email}
                  url={this.prependAPI + this.recoverPasswordUrl}
                />}
              {this.type === "signup" && this.stepIndex < STEPS.indexOf("payment") && !this.campusVersion && (
                <p class="step-indicator">Step {this.stepIndex + 1} / {this.paymentDetails ? 3 : 2}</p>
              )}
            </Fragment>
          }
        </div>
      </Host>
    );
  }
}
